import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, ArrowRight, FileText, Camera, Video } from "lucide-react";
import { Nav, Footer, Hinomaru } from "@/components/Nav";
import { getPaymentStatus, getBooking } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function PaymentSuccess() {
    const { t, lang } = useLang();
    const locale = lang === "en" ? "en-GB" : "it-IT";
    const [sp] = useSearchParams();
    const sessionId = sp.get("session_id");
    const [status, setStatus] = useState("polling");
    const [data, setData] = useState(null);
    const [booking, setBooking] = useState(null);

    useEffect(() => {
        let cancelled = false;
        let attempts = 0;
        const poll = async () => {
            if (cancelled) return;
            attempts++;
            try {
                const r = await getPaymentStatus(sessionId);
                setData(r);
                if (r.payment_status === "paid" && r.booking_id) {
                    setStatus("paid");
                    const b = await getBooking(r.booking_id, r.access_token);
                    setBooking(b);
                    return;
                }
                if (r.payment_status === "failed" || r.payment_status === "expired") {
                    setStatus("failed"); return;
                }
            } catch (e) {}
            if (attempts >= 20) { setStatus("timeout"); return; }
            setTimeout(poll, 2000);
        };
        if (sessionId) poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-[#15181C] text-[#F4F2ED]">
            <Nav />
            <div className="max-w-3xl mx-auto px-5 md:px-8 py-16">
                {status === "polling" && (
                    <div data-testid="success-polling" className="flex flex-col items-center py-24">
                        <Loader2 className="animate-spin text-[#E2231A] mb-6" size={64} strokeWidth={1.5} />
                        <div className="font-display text-3xl uppercase">{t("success.polling")}</div>
                    </div>
                )}
                {status === "failed" && (
                    <div data-testid="success-failed" className="border border-[#E2231A] p-8">
                        <div className="font-display text-4xl uppercase text-[#E2231A]">{t("success.failed")}</div>
                        <p className="text-[#9AA2AD] mt-3">{t("success.failedBody")}</p>
                        <Link to="/#prodotti" className="mt-6 inline-flex items-center gap-2 bg-[#E2231A] px-5 py-3 font-display uppercase tracking-wider">{t("success.retry")} <ArrowRight size={18} /></Link>
                    </div>
                )}
                {status === "paid" && booking && (
                    <div data-testid="success-paid">
                        <div className="flex items-center gap-3 mb-4">
                            <CheckCircle2 className="text-[#E2231A]" size={36} strokeWidth={1.5} />
                            <span className="text-xs uppercase tracking-[0.3em] text-[#9AA2AD]">{t("success.confirmed")}</span>
                        </div>
                        <h1 className="font-display text-5xl md:text-6xl uppercase mb-8">{t("success.title1")}<br/><span className="text-[#E2231A]">{t("success.title2")}</span></h1>

                        {/* Voucher card */}
                        <div data-testid="voucher-card" className="bg-[#F4F2ED] text-[#15181C] p-6 md:p-8 border-t-4 border-[#E2231A] relative">
                            {booking.manleva_signed && (
                                <div data-testid="verified-badge" className="absolute -top-4 right-4 md:right-8 bg-[#15181C] text-[#F4F2ED] px-4 py-2 flex items-center gap-2 border-l-4 border-[#E2231A] shadow-lg">
                                    <CheckCircle2 size={16} className="text-[#E2231A]" strokeWidth={2} />
                                    <span className="font-display text-sm uppercase tracking-widest">{t("success.verified")}</span>
                                </div>
                            )}
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#9AA2AD]">{t("success.voucher")}</div>
                                    <div data-testid="voucher-code" className="font-display text-3xl">{booking.voucher_code}</div>
                                </div>
                                {booking.turno_progressivo && (
                                    <div className="text-center mx-4">
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#9AA2AD]">{t("success.turn")}</div>
                                        <div data-testid="turno-progressivo" className="font-display text-5xl text-[#E2231A] leading-none">#{booking.turno_progressivo}</div>
                                    </div>
                                )}
                                <Hinomaru size={40} />
                            </div>
                            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
                                <div className="space-y-2">
                                    <Info label={t("success.name")} value={booking.customer_name} />
                                    <Info label={t("success.email")} value={booking.customer_email} />
                                    <Info label={t("success.circuit")} value={booking.circuit || "—"} />
                                    <Info label={t("success.formula")} value={booking.product_id.replace("_", " ").toUpperCase()} />
                    <Info label={t("success.slot")} value={booking.slots.map(s => `${s.car} · ${new Date(s.start_time).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`).join(" · ")} />
                                    <div className="flex gap-4 mt-2">
                                        {booking.addon_video && <span className="text-xs uppercase tracking-widest flex items-center gap-1"><Video size={14} strokeWidth={1.5} /> {t("success.video")}</span>}
                                        {booking.addon_foto && <span className="text-xs uppercase tracking-widest flex items-center gap-1"><Camera size={14} strokeWidth={1.5} /> {t("success.foto")}</span>}
                                    </div>
                                </div>
                                <img data-testid="voucher-qr" src={booking.qr} alt="QR Voucher" className="w-40 h-40 border border-[#15181C]/20" />
                            </div>
                        </div>

                        {!booking.manleva_signed && (
                        <div className="mt-8 border border-[#E2231A] p-6 bg-[#E2231A]/5">
                            <div className="flex items-center gap-3 mb-2"><FileText className="text-[#E2231A]" strokeWidth={1.5} /><h3 className="font-display text-2xl uppercase">{t("success.missingWaiver")}</h3></div>
                            <p className="text-sm text-[#9AA2AD] mb-4">{t("success.missingWaiverBody")}</p>
                            <Link data-testid="cta-manleva" to={`/manleva/${booking.id}?t=${encodeURIComponent(data?.access_token || "")}&sid=${encodeURIComponent(sessionId || "")}`} className="inline-flex items-center gap-2 bg-[#E2231A] hover:bg-[#C11B15] px-5 py-3 font-display text-lg uppercase tracking-wider transition-colors duration-150">
                                {t("success.completeWaiver")} <ArrowRight size={18} />
                            </Link>
                        </div>
                        )}
                        {booking.manleva_signed && (
                            <div data-testid="share-cta" className="mt-8 border border-[#9AA2AD]/20 p-6 bg-[#1C2026] text-center">
                                <p className="text-sm text-[#9AA2AD] uppercase tracking-widest mb-1">{t("success.done")}</p>
                                <p className="text-[#F4F2ED]">{t("success.shareBody")} <span className="text-[#E2231A] font-display">@velora.jdm</span></p>
                            </div>
                        )}
                    </div>
                )}
                {status === "timeout" && <div data-testid="success-timeout" className="text-[#9AA2AD]">{t("success.timeout")}</div>}
            </div>
            <Footer />
        </div>
    );
}

const Info = ({ label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#9AA2AD]">{label}</div>
        <div className="font-display text-lg">{value}</div>
    </div>
);
